import 'core-js/actual';
import 'intl-pluralrules';
import 'whatwg-fetch';

import { ApolloProvider } from '@apollo/client';
import { BrowserRouter, Switch } from 'react-router-dom';
import { ModalProvider } from 'react-modal-hook';
import { Suspense } from 'react';
import { createRoot } from 'react-dom/client';

import '../setup';

import { Locale } from '__generated__/graphql';
import { client, setHeaders } from '../api/apollo';
import { getLoginUrl } from '../common/Auth/useAuth';
import { setSupportedLocales, setupTranslations } from '../i18n';
import { withCurrentUrlParams } from '../common/helpers';
import AddProjectPage from './Projects/AddProjectPage';
import AppLayout from './Common/AppLayout';
import BrowserRedirect from './Common/BrowserRedirect';
import ConnectAppPage from './Projects/ConnectAppPage';
import CookieConsentProvider from '../common/Common/CookieConsentProvider';
import ExportsProvider from './Exports/ExportsProvider';
import GlobalStyle from '../common/GlobalStyle';
import GuestLayout from '../common/Common/GuestLayout';
import GuestOnlyRoute from './Common/GuestOnlyRoute';
import NotificationProvider from '../common/Notifications/NotificationProvider';
import ProjectRoute from './Common/ProjectRoute';
import ProjectRoutes from './Common/ProjectRoutes';
import RollbarProvider from '../common/Common/RollbarProvider';
import SessionProvider from './Auth/SessionProvider';
import SignupForm from './Auth/SignupForm';
import ThemeProvider from '../common/Common/ThemeProvider';
import UI from '../common/UI';
import UserRoute from './Common/UserRoute';

setSupportedLocales([Locale.en, Locale.nl, Locale.de, Locale.fr]);

setupTranslations({
  namespaces: [
    'backend',
    'common',
  ],
});

setHeaders((headers) => ({
  ...headers,
  'Atleta-Guard': 'Backend',
}));

const App = () => (
  <RollbarProvider>
    <ThemeProvider options={{ scrollOffset: { sm: 0, md: 60 } }}>
      <GlobalStyle />
      <Suspense fallback={<UI.PageLoader />}>
        <CookieConsentProvider>
          <ApolloProvider client={client}>
            <SessionProvider>
              <NotificationProvider>
                <BrowserRouter basename="/b">
                  <Switch>
                    {/* Guest routes (redirect users) */}
                    <GuestOnlyRoute
                      path="/register"
                      redirectToLastProject
                      render={() => (
                        <GuestLayout>
                          <SignupForm />
                        </GuestLayout>
                      )}
                    />

                    {/* Legacy /b/login redirect */}
                    <GuestOnlyRoute
                      path="/login"
                      redirectToLastProject
                      render={() => <BrowserRedirect to={withCurrentUrlParams(getLoginUrl())} />}
                    />

                    {/* User routes (project optional) */}
                    <UserRoute
                      path="/projects/add"
                      render={() => (
                        <ModalProvider>
                          <AppLayout>
                            <AddProjectPage />
                          </AppLayout>
                        </ModalProvider>
                      )}
                    />
                    <UserRoute
                      path="/connect/:appId"
                      render={({ match: { params: { appId } } }) => (
                        <ModalProvider>
                          <AppLayout>
                            <ConnectAppPage appId={appId} />
                          </AppLayout>
                        </ModalProvider>
                      )}
                    />

                    {/* User routes (project required) */}
                    <ProjectRoute
                      path="/:projectId"
                      render={({ match: { params: { projectId } } }) => (
                        <ModalProvider>
                          <ExportsProvider projectId={projectId}>
                            <AppLayout>
                              <ProjectRoutes projectId={projectId} />
                            </AppLayout>
                          </ExportsProvider>
                        </ModalProvider>
                      )}
                    />

                    {/* Redirect Guests and Users */}
                    <GuestOnlyRoute
                      path="/"
                      redirectToLastProject
                      render={() => (
                        <BrowserRedirect to={getLoginUrl({ returnUrl: window.location.href })} />
                      )}
                    />
                  </Switch>
                </BrowserRouter>
              </NotificationProvider>
            </SessionProvider>
          </ApolloProvider>
        </CookieConsentProvider>
      </Suspense>
    </ThemeProvider>
  </RollbarProvider>
);

const container = document.getElementById('app');
const root = createRoot(container);
root.render(<App />);
